import { useCallback, useMemo } from 'react';
import {
  DatePicker,
  DateField,
  Calendar,
  Label,
  FieldError,
  type DatePickerProps
} from '@heroui/react';
import type { DateValue } from 'react-aria-components';

import {
  FieldValues,
  Path,
  useController,
  UseControllerProps
} from 'react-hook-form';
import { useGlobalComponent } from '@rhino-project/core/hooks';
import { stripRhinoProps } from './utils/stripRhinoProps';
import {
  CalendarDate,
  CalendarDateTime,
  ZonedDateTime,
  getLocalTimeZone,
  now,
  parseAbsoluteToLocal
} from '@internationalized/date';

export type FieldDatePickerProps<T extends FieldValues = FieldValues> = Omit<
  DatePickerProps<DateValue>,
  'name' | 'children'
> &
  Omit<UseControllerProps<T>, 'name' | 'control'> & {
    parse?: (
      value: string
    ) => ZonedDateTime | CalendarDate | CalendarDateTime | null | undefined;
    /**
     * The path inside the form object.
     */
    path: Path<T>;
    /**
     * Label text for the field.
     */
    label?: string;
  };

export const FieldDatePickerBase = <T extends FieldValues = FieldValues>({
  path,
  parse = parseAbsoluteToLocal,
  label,
  variant,
  ...props
}: FieldDatePickerProps<T> & { variant?: 'primary' | 'secondary' }) => {
  const {
    field: { onChange, disabled, value: fieldValue, ...fieldProps },
    fieldState: { error }
  } = useController({ name: path });

  const placeholderValue = useMemo(() => {
    const date = now(getLocalTimeZone());
    return date.set({ millisecond: 0 });
  }, []);

  const value = useMemo(
    () => (fieldValue ? parse(fieldValue) : null),
    [fieldValue, parse]
  );

  const handleOnChange = useCallback(
    (newValue: DateValue | null) => {
      // eslint-disable-next-line no-useless-assignment -- Keep the initializer unchanged for the isolated tooling upgrade.
      let stringValue = undefined;

      // Only ZonedDateTime carries toAbsoluteString, so the throw is what
      // selects the fallback - the casts assert what the try is testing.
      try {
        stringValue = (newValue as ZonedDateTime).toAbsoluteString();
      } catch {
        try {
          stringValue = (newValue as DateValue).toString();
        } catch {
          stringValue = null;
        }
      }

      onChange(stringValue);
    },
    [onChange]
  );

  return (
    <DatePicker
      {...fieldProps}
      granularity="second"
      placeholderValue={placeholderValue}
      hideTimeZone
      isInvalid={!!error}
      isDisabled={disabled}
      onChange={handleOnChange}
      value={value}
      className="w-full"
      {...stripRhinoProps(props)}
    >
      {label && <Label>{label}</Label>}
      <DateField.Group fullWidth variant={variant}>
        <DateField.Input>
          {(segment) => <DateField.Segment segment={segment} />}
        </DateField.Input>
        <DateField.Suffix>
          <DatePicker.Trigger>
            <DatePicker.TriggerIndicator />
          </DatePicker.Trigger>
        </DateField.Suffix>
      </DateField.Group>
      <DatePicker.Popover className="min-w-80">
        <Calendar>
          <Calendar.Header>
            <Calendar.YearPickerTrigger>
              <Calendar.YearPickerTriggerHeading />
              <Calendar.YearPickerTriggerIndicator />
            </Calendar.YearPickerTrigger>
            <Calendar.NavButton slot="previous" />
            <Calendar.NavButton slot="next" />
          </Calendar.Header>
          <Calendar.Grid>
            <Calendar.GridHeader>
              {(day: string) => (
                <Calendar.HeaderCell>{day}</Calendar.HeaderCell>
              )}
            </Calendar.GridHeader>
            <Calendar.GridBody>
              {(date) => <Calendar.Cell date={date} />}
            </Calendar.GridBody>
          </Calendar.Grid>
        </Calendar>
      </DatePicker.Popover>
      <FieldError>{error?.message}</FieldError>
    </DatePicker>
  );
};

export const FieldDatePicker = (props: FieldDatePickerProps) =>
  useGlobalComponent('FieldDatePicker', FieldDatePickerBase, props);
